/**
 * Face and body zones: the "teeth" of dermatology and aesthetic medicine.
 *
 * A dental chart records findings and work per tooth. A derma or aesthetic
 * clinic records them per ZONE: the glabella that took 20 units of toxin, the
 * left cheek with melasma, the right flank in a laser-hair-removal course.
 * This module is the one list of zones the server validates against and the
 * interface draws, in both languages, so a zone recorded in Damascus and read
 * in Dubai means the same place.
 *
 * CLINICAL SAFETY NOTE — the same rule as the dental chart: the figure is
 * drawn as the clinician sees the patient. Face on, the patient's RIGHT is on
 * the viewer's LEFT. From behind, the patient's right is on the viewer's
 * right. `side` below is the PATIENT's side, always; the drawing decides where
 * to put it, and the interface direction (Arabic, right-to-left) must never
 * mirror it.
 */

export type Region = 'face' | 'body';
export type ZoneSide = 'right' | 'left' | 'midline';
export type ZoneView = 'front' | 'back' | 'both';

export interface Zone {
  code: string;
  region: Region;
  side: ZoneSide;
  /** Which drawing shows it: the front figure, the back figure, or both. */
  view: ZoneView;
  /** A grouping for lists and reports: 'upper_face', 'arms'… */
  group: string;
}

const face = (code: string, side: ZoneSide, group: string): Zone => ({ code, region: 'face', side, view: 'front', group });
const body = (code: string, side: ZoneSide, view: ZoneView, group: string): Zone => ({ code, region: 'body', side, view, group });

/** The face and neck, as injectors and dermatologists divide them. */
export const FACE_ZONES: readonly Zone[] = [
  face('scalp', 'midline', 'upper_face'),
  face('forehead', 'midline', 'upper_face'),
  face('glabella', 'midline', 'upper_face'),
  face('temple_r', 'right', 'upper_face'),
  face('temple_l', 'left', 'upper_face'),
  face('brow_r', 'right', 'upper_face'),
  face('brow_l', 'left', 'upper_face'),
  face('periorbital_r', 'right', 'eyes'),
  face('periorbital_l', 'left', 'eyes'),
  face('tear_trough_r', 'right', 'eyes'),
  face('tear_trough_l', 'left', 'eyes'),
  face('nose', 'midline', 'mid_face'),
  face('cheek_r', 'right', 'mid_face'),
  face('cheek_l', 'left', 'mid_face'),
  face('nasolabial_r', 'right', 'mid_face'),
  face('nasolabial_l', 'left', 'mid_face'),
  face('upper_lip', 'midline', 'lips'),
  face('lower_lip', 'midline', 'lips'),
  face('perioral', 'midline', 'lips'),
  face('marionette_r', 'right', 'lower_face'),
  face('marionette_l', 'left', 'lower_face'),
  face('chin', 'midline', 'lower_face'),
  face('jawline_r', 'right', 'lower_face'),
  face('jawline_l', 'left', 'lower_face'),
  face('masseter_r', 'right', 'lower_face'),
  face('masseter_l', 'left', 'lower_face'),
  face('neck', 'midline', 'neck'),
];

/** The body, front and back, as laser, contouring and dermatology use it. */
export const BODY_ZONES: readonly Zone[] = [
  body('decolletage', 'midline', 'front', 'trunk'),
  body('shoulder_r', 'right', 'both', 'arms'),
  body('shoulder_l', 'left', 'both', 'arms'),
  body('chest', 'midline', 'front', 'trunk'),
  body('abdomen', 'midline', 'front', 'trunk'),
  body('flank_r', 'right', 'both', 'trunk'),
  body('flank_l', 'left', 'both', 'trunk'),
  body('back_upper', 'midline', 'back', 'trunk'),
  body('back_lower', 'midline', 'back', 'trunk'),
  body('arm_upper_r', 'right', 'both', 'arms'),
  body('arm_upper_l', 'left', 'both', 'arms'),
  body('forearm_r', 'right', 'both', 'arms'),
  body('forearm_l', 'left', 'both', 'arms'),
  body('hand_r', 'right', 'both', 'arms'),
  body('hand_l', 'left', 'both', 'arms'),
  body('underarm_r', 'right', 'front', 'arms'),
  body('underarm_l', 'left', 'front', 'arms'),
  body('bikini', 'midline', 'front', 'trunk'),
  body('buttock_r', 'right', 'back', 'legs'),
  body('buttock_l', 'left', 'back', 'legs'),
  body('thigh_r', 'right', 'both', 'legs'),
  body('thigh_l', 'left', 'both', 'legs'),
  body('knee_r', 'right', 'both', 'legs'),
  body('knee_l', 'left', 'both', 'legs'),
  body('lower_leg_r', 'right', 'both', 'legs'),
  body('lower_leg_l', 'left', 'both', 'legs'),
  body('foot_r', 'right', 'both', 'legs'),
  body('foot_l', 'left', 'both', 'legs'),
];

export const ZONES: readonly Zone[] = [...FACE_ZONES, ...BODY_ZONES];
const BY_CODE = new Map(ZONES.map((z) => [z.code, z]));

export function zoneOf(code: string): Zone | null {
  return BY_CODE.get(code) ?? null;
}

export function isValidZone(code: string): boolean {
  return BY_CODE.has(code);
}

export function regionOf(code: string): Region | null {
  return BY_CODE.get(code)?.region ?? null;
}

/**
 * What a clinician finds on a zone. The skin's conditions and the face's
 * ageing, in the words a chart uses. A finding stays current for its zone
 * until a later finding replaces it.
 */
export const ZONE_FINDINGS = [
  'normal',
  'wrinkles_dynamic',
  'wrinkles_static',
  'volume_loss',
  'laxity',
  'asymmetry',
  'pigmentation',
  'melasma',
  'acne',
  'acne_scars',
  'scar',
  'lesion',
  'mole',
  'redness',
  'rosacea',
  'enlarged_pores',
  'unwanted_hair',
  'hair_loss',
  'cellulite',
  'fat_deposit',
  'stretch_marks',
  'tattoo',
  'other',
] as const;
export type ZoneFinding = (typeof ZONE_FINDINGS)[number];

/**
 * What a completed procedure leaves on the zone: the mark the chart shows,
 * the way a crown or a filling shows on a tooth. A procedure in the catalogue
 * declares its mark; completing it writes the mark with the dose.
 */
export const ZONE_MARKS = [
  'toxin',
  'filler',
  'booster',
  'prp',
  'mesotherapy',
  'thread',
  'laser',
  'ipl',
  'peel',
  'microneedling',
  'rf',
  'hifu',
  'cryolipolysis',
  'excision',
  'cryotherapy',
  'electrocautery',
  'biopsy',
  'injection_other',
  'other',
] as const;
export type ZoneMark = (typeof ZONE_MARKS)[number];

/** How a dose is counted. Units of toxin, millilitres of filler, shots of laser… */
export const DOSE_UNITS = ['units', 'ml', 'mg', 'sessions', 'shots', 'threads', 'cm2', 'vials', 'minutes'] as const;
export type DoseUnit = (typeof DOSE_UNITS)[number];

export const SEVERITIES = ['mild', 'moderate', 'severe'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const SPECIALTIES = ['dental', 'dermatology', 'aesthetics'] as const;
export type Specialty = (typeof SPECIALTIES)[number];

/** A zone's name as the audit log and exports print it: 'cheek_l' → 'left cheek'. */
export function zoneLabel(code: string): string {
  const z = zoneOf(code);
  if (!z) return code;
  const base = code.replace(/_(r|l)$/, '').replace(/_/g, ' ');
  return z.side === 'midline' ? base : `${z.side} ${base}`;
}
